<template>
  <div class="engine-input-debugger">
    <!-- Recherche -->
    <div class="mb-3 position-relative">
      <i class="fad fa-magnifying-glass position-absolute text-muted" style="left:10px; top:50%; transform:translateY(-50%)"></i>
      <input
        v-model="search"
        type="text"
        class="form-control form-control-sm ps-10"
        placeholder="Rechercher une propriété ou une valeur…"
      />
      <i
        v-if="search"
        class="fad fa-times position-absolute text-muted cursor-pointer"
        style="right:10px; top:50%; transform:translateY(-50%)"
        @click="search = ''"
      ></i>
    </div>

    <!-- Résultats de recherche -->
    <div v-if="search" class="search-results">
      <div v-if="searchResults.length === 0" class="text-muted fst-italic py-2">Aucun résultat.</div>
      <div
        v-for="(r, i) in searchResults"
        :key="'sr' + i"
        class="d-flex justify-content-between align-items-center py-2 px-2 rounded bg-hover-light-primary cursor-pointer"
        @click="goTo(r.uuid)"
      >
        <span class="text-truncate" style="max-width:60%">
          <span class="badge badge-light-primary me-2">{{ itemLabel(r.uuid) }}</span>
          <span v-html="highlight(r.key)"></span>
        </span>
        <span class="text-muted text-truncate" style="max-width:40%" v-html="highlight(formatVal(r.value))"></span>
      </div>
    </div>

    <template v-else>
      <!-- Fil d'Ariane -->
      <div v-if="trail.length" class="d-flex align-items-center flex-wrap mb-3" style="gap:4px">
        <template v-for="(uuid, i) in trail">
          <i v-if="i > 0" :key="'sep' + i" class="fad fa-chevron-right fs-9 text-muted"></i>
          <span
            :key="'bc' + i"
            class="badge cursor-pointer"
            :class="i === trail.length - 1 ? 'badge-primary' : 'badge-light-primary'"
            @click="trail = trail.slice(0, i + 1)"
          >{{ itemLabel(uuid) }}</span>
        </template>
      </div>

      <!-- Objet courant -->
      <div v-if="currentItem" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="fw-bolder fs-6">
            {{ currentItem.name || currentItem.modelCode }}
            <span class="badge badge-light-dark ms-1">{{ currentItem.modelCode }}</span>
          </div>
          <span class="text-muted" style="font-size:9px">{{ currentUuid }}</span>
        </div>
        <table class="table table-sm table-striped m-0 align-middle">
          <tbody>
            <tr v-for="(val, key) in currentItem.properties" :key="'p' + key">
              <td class="fw-bold text-nowrap" style="width:42%; vertical-align:top">{{ key }}</td>
              <td>
                <template v-if="refUuids(val).length">
                  <span
                    v-for="(u, j) in refUuids(val)"
                    :key="'ref' + j"
                    class="badge badge-light-info cursor-pointer me-1 mb-1"
                    @click="goTo(u)"
                  ><i class="fad fa-arrow-right-to-bracket me-1"></i>{{ itemLabel(u) }}</span>
                </template>
                <span v-else :class="isEmpty(val) ? 'text-muted fst-italic' : 'fw-bold'">{{ formatVal(val) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Saut rapide vers tous les objets -->
      <div class="mb-4">
        <div class="fw-bolder mb-2 text-muted"><i class="fad fa-cubes me-1"></i>Objets ({{ itemCount }})</div>
        <span
          v-for="(it, uuid) in (value && value.items) || {}"
          :key="'all' + uuid"
          class="badge cursor-pointer me-1 mb-1"
          :class="uuid === currentUuid ? 'badge-primary' : 'badge-light'"
          @click="goTo(uuid)"
        >{{ it.name || it.modelCode }}</span>
      </div>

      <!-- Payload -->
      <div v-if="value && value.payload" class="mb-4">
        <div class="fw-bolder mb-2 text-muted"><i class="fad fa-paper-plane me-1"></i>Payload</div>
        <table class="table table-sm table-striped m-0">
          <tbody>
            <tr v-for="(val, key) in value.payload" :key="'pl' + key">
              <td class="fw-bold text-nowrap" style="width:42%">{{ key }}</td>
              <td>
                <template v-if="refUuids(val).length">
                  <span
                    v-for="(u, j) in refUuids(val)"
                    :key="'plref' + j"
                    class="badge badge-light-info cursor-pointer me-1 mb-1"
                    @click="goTo(u)"
                  ><i class="fad fa-arrow-right-to-bracket me-1"></i>{{ itemLabel(u) }}</span>
                </template>
                <span v-else>{{ formatVal(val) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Assureurs -->
      <div v-if="value && value.insurers">
        <div class="fw-bolder mb-2 text-muted"><i class="fad fa-building-shield me-1"></i>Assureurs ({{ value.insurers.length }})</div>
        <span v-for="(ins, i) in value.insurers" :key="'ins' + i" class="badge badge-light me-1 mb-1">{{ ins }}</span>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component
export default class EngineInputDebugger extends Vue {
  @Prop() value!: any;
  search = '';
  trail: string[] = [];

  mounted() {
    this.resetTrail();
  }

  @Watch('value')
  onValue() {
    this.resetTrail();
  }

  resetTrail() {
    const root = this.value?.itemUuid || (this.value?.items ? Object.keys(this.value.items)[0] : null);
    this.trail = root ? [root] : [];
  }

  get itemCount(): number {
    return this.value?.items ? Object.keys(this.value.items).length : 0;
  }

  get currentUuid(): string | null {
    return this.trail.length ? this.trail[this.trail.length - 1] : null;
  }

  get currentItem(): any {
    return this.currentUuid && this.value?.items ? this.value.items[this.currentUuid] : null;
  }

  itemLabel(uuid: string): string {
    const it = this.value?.items?.[uuid];
    return it ? it.name || it.modelCode || uuid : uuid;
  }

  // Une valeur est-elle l'UUID d'un autre item (référence navigable) ?
  isRef(v: any): boolean {
    return typeof v === 'string' && !!this.value?.items?.[v];
  }

  refUuids(val: any): string[] {
    if (this.isRef(val)) return [val];
    if (Array.isArray(val)) return val.filter((x: any) => this.isRef(x));
    return [];
  }

  isEmpty(v: any): boolean {
    return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
  }

  formatVal(v: any): string {
    if (v === null || v === undefined) return '—';
    if (v === '') return '(vide)';
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    if (Array.isArray(v)) return v.length ? JSON.stringify(v) : '[]';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

  goTo(uuid: string) {
    if (!uuid || !this.value?.items?.[uuid]) return;
    const idx = this.trail.indexOf(uuid);
    this.trail = idx >= 0 ? this.trail.slice(0, idx + 1) : [...this.trail, uuid];
    this.search = '';
  }

  get searchResults(): any[] {
    const q = this.search.trim().toLowerCase();
    if (!q || !this.value?.items) return [];
    const out: any[] = [];
    for (const uuid of Object.keys(this.value.items)) {
      const it = this.value.items[uuid];
      const props = it.properties || {};
      for (const key of Object.keys(props)) {
        const val = props[key];
        const valStr = this.formatVal(val).toLowerCase();
        if (
          key.toLowerCase().includes(q) ||
          valStr.includes(q) ||
          (it.modelCode || '').toLowerCase().includes(q) ||
          (it.name || '').toLowerCase().includes(q)
        ) {
          out.push({ uuid, key, value: val });
        }
      }
    }
    return out.slice(0, 100);
  }

  highlight(text: string): string {
    const q = this.search.trim();
    const safe = String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
    if (!q) return safe;
    const esc = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safe.replace(new RegExp('(' + esc + ')', 'ig'), '<mark>$1</mark>');
  }
}
</script>

<style scoped>
.engine-input-debugger {
  font-size: 12px;
}
.engine-input-debugger table td {
  word-break: break-word;
}
.search-results {
  max-height: 100%;
}
</style>
